<template>
  <div>
    <q-list v-if="customer" no-border>
      <q-item class="q-pa-none">
        <q-item-main :label="customerName" :sublabel="customer.customer_number"/>

        <q-item-side right>
          <q-btn icon="close" flat @click="handleCustomerDeselect" :disable="disable"/>
        </q-item-side>
      </q-item>
    </q-list>

    <q-field
      v-if="!customer"
      :error="hasError"
      :error-label="errorLabel"
      class="q-ma-none"
    >
      <q-search
        v-model="customerInput"
        name="customer-search"
        :float-label="$t('expense_supplier')"
        :disable="disable"
        color="white"
        class="no-shadow"
        clearable
      >
        <infinite-autocomplete
          ref="autocomplete"
          @fetch="handleInfiniteScroll"
          @search="handleSearch"
          @selected="handleSelect"
          :debounce="defaultSearchDebounce"
          :min-characters="0"
          :max-results="200"
          :max-height="'300px'"
        >
          <div class="row gutter-xs justify-center q-ma-sm">
            <q-btn class="col" color="primary" flat @click="handleAddCustomer('person')">{{ $t('create_person') }}</q-btn>
            <q-btn class="col" color="primary" flat @click="handleAddCustomer('company')">{{ $t('create_company') }}</q-btn>
          </div>
        </infinite-autocomplete>
      </q-search>
    </q-field>

    <default-modal
      v-model="modalOpened"
      name="add-customer-modal"
      :autofocus="$refs.addCustomerForm"
      :successHandler="handleStoreCustomer"
      :title="title"
      width="sm"
    >
      <component :is="formComponent" ref="addCustomerForm" slot="content" v-model="formData" :action="action" :restrictToBusinessPartnerType="businessPartnerType"/>
    </default-modal>
  </div>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex'
  import AddCompanyForm from 'components/Customers/Companies/AddCompanyForm.vue'
  import AddPersonForm from 'components/Customers/Persons/AddPersonForm.vue'
  import CustomerAddress from 'src/utils/SubResources/CustomerAddress'
  import { CustomersClient } from 'src/api/Customers'
  import DefaultModal from 'components/Modal/DefaultModal.vue'
  import InfiniteAutocomplete from 'components/Common/InfiniteAutocomplete.vue'
  import { businessPartnerTypes } from 'src/utils/businessPartnerTypes'
  import changeCase from 'change-case-object'
  import cleanDeep from 'clean-deep'
  import { createErrorFromResponse } from 'src/utils/error'
  import { customerTypes } from 'src/utils/customerTypes'
  import debounceMixin from 'src/utils/mixins/debounce'
  import { getCustomerName } from 'src/utils/searchResultParser'
  import { pick } from 'lodash'

  export default {
    $_veeValidate: {
      validator: 'new'
    },

    data () {
      return {
        customerInput: '',
        customer: null,
        modalOpened: false,
        type: null,
        formData: null,
        action: null
      }
    },

    props: {
      value: {
        required: true
      },
      hasError: {
        required: false,
        default: false
      },
      errorLabel: {
        required: false,
        default: null
      },
      disable: {
        required: false,
        default: false
      },
      types: {
        required: false,
        default: () => [customerTypes.COMPANY, customerTypes.PERSON],
        type: Array
      }
    },

    computed: {
      ...mapGetters(['customerModalData']),

      businessPartnerType () {
        return businessPartnerTypes.SUPPLIER
      },

      customerName () {
        if (!this.customer) {
          return null
        }

        return getCustomerName(this.customer)
      },

      title () {
        if (!this.action || !this.type) {
          return
        }

        return this.$t([this.action, this.type].join('_'))
      },

      formComponent () {
        switch (this.type) {
        case 'company':
          return 'addCompanyForm'
        case 'person':
          return 'addPersonForm'
        }
      },

      storeMethod () {
        const methods = {
          company: { create: this.createCompany, update: this.updateCompany },
          person: { create: this.createPerson, update: this.updatePerson }
        }

        const method = methods[this.type][this.action]
        if (!method) {
          throw new Error('Invalid action or type')
        }

        return method
      },

      filters () {
        return [
          ['business_partner_type', '=', [businessPartnerTypes.SUPPLIER, businessPartnerTypes.CUSTOMER_AND_SUPPLIER]],
          ['type', '=', this.types]
        ]
      }
    },

    methods: {
      ...mapActions(['createCompany', 'createPerson', 'updateCompany', 'updatePerson', 'fetchCustomer', 'setOpenEditCustomerModal']),

      handleSearch (query, done) {
        this.executeSearch(query)
          .then(response => {
            const isLastPage = response.data.meta.pagination.current_page === response.data.meta.pagination.total_pages

            done(this.mapSearchResponse(response.data.data), isLastPage)
          })
      },

      executeSearch (term) {
        const client = new CustomersClient()
          .filter(this.filters)
          .include(['addresses'])

        if (!term) {
          return client.sort('name:asc')
            .list()
        }

        return client.search(term)
      },

      handleInfiniteScroll (query, index, done) {
        if (query) {
          done([], true)
          return
        }

        new CustomersClient()
          .filter(this.filters)
          .include(['addresses'])
          .sort('name:asc')
          .page(index + 1)
          .list()
          .then(response => {
            const isLastPage = response.data.meta.pagination.current_page === response.data.meta.pagination.total_pages

            return done(this.mapSearchResponse(response.data.data), isLastPage)
          })
      },

      mapSearchResponse (data) {
        return data.map(entry => {
          return {
            value: entry,
            label: getCustomerName(entry),
            sublabel: `#${entry.customer_number}`,
            icon: entry.type === 'person' ? 'person' : 'business'
          }
        })
      },

      handleSelect (item) {
        this.customerInput = item.label
        this.customer = item.value
      },

      handleCustomerDeselect () {
        this.customerInput = ''
        this.customer = null
      },

      handleAddCustomer (type) {
        this.type = type
        this.action = 'create'

        if (this.$refs.addCustomerForm) {
          this.$refs.addCustomerForm.resetData()
        }

        this.$refs.autocomplete.hide()
          .then(() => {
            // Open next tick, otherwise autofocus will not work
            this.$nextTick(() => {
              this.modalOpened = true
            })
          })
      },

      handleStoreCustomer () {
        this.storeCustomer()
      },

      storeCustomer () {
        let data = changeCase.snakeCase(this.formData)

        const customerAddress = new CustomerAddress()

        if (data.addresses) {
          data.addresses = data.addresses.filter(address => {
            return !(customerAddress.isNew(address) && customerAddress.isEmpty(address))
          })
        }

        this.storeMethod(this.cleanData(data))
          .then((customer) => {
            this.customerInput = getCustomerName(customer)
            this.customer = customer
            this.modalOpened = false
          })
          .catch(createErrorFromResponse)
      },

      cleanData (data) {
        // When patching a customer we do not remove null values, otherwise we can not remove a company from a person.
        const cleanedData = this.action === 'create' ? cleanDeep(data) : cleanDeep(data, { nullValues: false })

        if (this.action === 'create') {
          return cleanedData
        }

        // ToDo: Not possible to reset accounts numbers du to them being an empty string when cleared
        return pick(
          cleanedData,
          'id',
          'company_id',
          'business_partner_type',
          'customer_number',
          'industry',
          'language',
          'name',
          'vat_number',
          'tax_number',
          'website',
          'department',
          'position',
          'salutation',
          'first_name',
          'last_name',
          'default_payment_deadline',
          'title',
          'accounts_receivable_number',
          'accounts_payable_number'
        )
      },

      open ({ type, id }) {
        this.$refs.addCustomerForm.resetData()
        this.setOpenEditCustomerModal({ type, id })
      }
    },

    watch: {
      customer () {
        this.$emit('input', this.customer)
      },

      customerInput (newVal) {
        if (newVal === '') {
          this.$emit('input', null)
        }
      },

      value: {
        handler: function (val) {
          this.customer = val
          this.customerInput = val ? (getCustomerName(val)) : ''
        },
        immediate: true
      }
    },

    components: {
      InfiniteAutocomplete,
      AddCompanyForm,
      AddPersonForm,
      DefaultModal
    },

    mixins: [
      debounceMixin
    ]
  }
</script>
